// src/pages/TasksPage.tsx
import { useState, useEffect, useMemo } from "react"
import { useAuthStore } from "@/store/authStore"
import { toast } from "@/hooks/use-toast"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { TaskList } from '@/components/tasks/TaskList'
import TaskCreateDialog from '@/components/tasks/TaskCreateDialog'
import BlockCreateDialog from '@/components/tasks/BlockCreateDialog'
import { TaskDetailDialog } from '@/components/tasks/TaskDetailDialog'
import { BlockTaskDialog } from '@/components/tasks/BlockTaskDialog'
import { Button } from "@/components/ui/button"
import type { ExtendedTask } from "@/types/task"
import { db } from "@/lib/firebase"
import { collection, onSnapshot } from "firebase/firestore"
import { Timestamp } from "firebase/firestore"
import TaskStatisticsDialog from '@/components/tasks/TaskStatisticsDialog'

// === ДИЗАЙН-СЛОЙ ===
import { tasksPageTheme } from '@/components/tasks/ui/TasksPageUI'
import { cn } from "@/lib/utils"   // ← добавили для cn()

interface ExtendedTask extends Task {
  subTasks?: ExtendedTask[]
  unreadBy?: number[]
  unreadMessagesBy?: Record<number, number>
  coAssigneeIds?: number[]
  coAssignees?: string[]
  observerIds?: number[]
  observers?: string[]
  isBlock?: boolean
  approvalStatus?: "new" | "accepted" | "in_review" | "rework" | "approved"
  approvalCount?: number
  acceptedByExecutor?: boolean
  approvedBy?: number[]
  approvedByCreator?: boolean
  parentId?: string
  urgency?: "urgent" | "planned"
  difficulty?: "easy" | "medium" | "hard"
  reminderInterval?: string
}

const getTimestamp = (ts: any): number => {
  if (!ts) return 0
  if (ts instanceof Timestamp) return ts.toDate().getTime()
  if (typeof ts === "string") return new Date(ts).getTime()
  if (ts.seconds) return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000
  return 0
}

export default function TasksPage() {
  const { user } = useAuthStore()
  const [tasks, setTasks] = useState<ExtendedTask[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<"active" | "archive" | "all">("active")
  const [selectedBlock, setSelectedBlock] = useState<ExtendedTask | null>(null)
  const [detailTask, setDetailTask] = useState<ExtendedTask | null>(null)
  const [showStatistics, setShowStatistics] = useState(false)

  const [deadlineFilter, setDeadlineFilter] = useState<
    "all" | "overdue" | "today" | "tomorrow" | "thisWeek" | "nextWeek" | "noDeadline"
  >("all")

  const userId = user ? Number(user.id) : 0
  const isAdmin = [1, 2, 4].includes(userId)
  const canViewStatistics = [1, 2, 3, 4].includes(userId)
  const canCreateTask = userId > 0

  const CAN_ASSIGN_TO: Record<number, number[]> = {
    1: [1, 2, 3, 4, 5, 6, 7],
    2: [2, 3, 4, 5, 6, 7],
    3: [3, 5, 6, 7],
    4: [4, 6, 7],
    5: [],
    6: [],
    7: [],
  }

  const getDeadlineCategory = (task: ExtendedTask): string => {
    if (!task.deadline) return "noDeadline"
    const deadlineDate = new Date(task.deadline)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const diffTime = deadlineDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24))

    if (diffDays < 0) return "overdue"
    if (diffDays === 0) return "today"
    if (diffDays === 1) return "tomorrow"
    if (diffDays <= 7) return "thisWeek"
    if (diffDays <= 14) return "nextWeek"
    return "noDeadline"
  }

  const taskTree = useMemo(() => {
    const map = new Map<string, ExtendedTask>()
    tasks.forEach((t) => map.set(t.id!, { ...t, subTasks: [] as ExtendedTask[] }))

    const roots: ExtendedTask[] = []
    tasks.forEach((t) => {
      if (t.parentId && map.has(t.parentId)) {
        map.get(t.parentId)!.subTasks!.push(map.get(t.id!)!)
      } else {
        roots.push(map.get(t.id!)!)
      }
    })
    return roots
  }, [tasks])

  const userInvolvedTasks = useMemo(() => {
    return taskTree.filter((t) => {
      const creatorMatch = Number(t.createdById) === userId
      const assigneeMatch = Number(t.assigneeId) === userId
      const coAssigneeMatch = t.coAssigneeIds?.includes(userId) || false
      const observerMatch = t.observerIds?.includes(userId) || false
      const historyMatch = t.history?.some((h) => Number(h.userId) === userId) || false

      const subMatch = t.subTasks?.some((sub) => {
        return (
          Number(sub.createdById) === userId ||
          Number(sub.assigneeId) === userId ||
          sub.coAssigneeIds?.includes(userId) ||
          sub.observerIds?.includes(userId) ||
          sub.history?.some((h) => Number(h.userId) === userId)
        )
      }) || false

      return creatorMatch || assigneeMatch || coAssigneeMatch || observerMatch || historyMatch || subMatch
    })
  }, [taskTree, userId])

  const userActiveTasks = userInvolvedTasks.filter((t) => t.approvalStatus !== "approved")
  const userArchivedTasks = userInvolvedTasks.filter((t) => t.approvalStatus === "approved")

  const displayedTasks = useMemo(() => {
    if (isAdmin && activeTab === "all") return tasks
    return activeTab === "active" ? userActiveTasks : userArchivedTasks
  }, [isAdmin, activeTab, tasks, userActiveTasks, userArchivedTasks])

  const filteredByDeadline = useMemo(() => {
    if (deadlineFilter === "all") return displayedTasks
    return displayedTasks.filter((task) => getDeadlineCategory(task) === deadlineFilter)
  }, [displayedTasks, deadlineFilter])

  const blocks = filteredByDeadline.filter((t) => t.isBlock)
  const regularTasks = filteredByDeadline.filter((t) => !t.isBlock)

  const sortedBlocks = [...blocks].sort((a, b) => {
    if (a.urgency === "urgent" && b.urgency !== "urgent") return -1
    if (a.urgency !== "urgent" && b.urgency === "urgent") return 1
    return getTimestamp(b.createdAt) - getTimestamp(a.createdAt)
  })

  const sortedRegularTasks = [...regularTasks].sort((a, b) => {
    if (a.urgency === "urgent" && b.urgency !== "urgent") return -1
    if (a.urgency !== "urgent" && b.urgency === "urgent") return 1
    return getTimestamp(b.createdAt) - getTimestamp(a.createdAt)
  })

  const onTaskUpdated = () => {}

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    setLoading(true)
    const tasksRef = collection(db, "tasks")

    const unsubscribe = onSnapshot(
      tasksRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        const loadedTasks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }) as ExtendedTask)
        setTasks(loadedTasks)
        setLoading(false)
      },
      (error) => {
        console.error("Ошибка onSnapshot:", error)
        toast({ title: "Ошибка", description: "Не удалось загрузить задачи", variant: "destructive" })
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [user])

  if (loading) {
    return (
      <div className={tasksPageTheme.loadingScreen}>
        <p className={tasksPageTheme.loadingText}>Загрузка задач из облака...</p>
      </div>
    )
  }

  return (
    <div className={tasksPageTheme.pageContainer}>
      <div className={tasksPageTheme.contentWrapper}>
        <div className={tasksPageTheme.header}>
          <h1 className={tasksPageTheme.pageTitle}>
            Мои задачи
          </h1>

          <div className={tasksPageTheme.actionsContainer}>
            {canCreateTask && (
              <>
                <TaskCreateDialog>
                  <Button 
                    className={cn(tasksPageTheme.triggerButton, tasksPageTheme.createTaskButton)}
                  >
                    + Поставить задачу
                  </Button>
                </TaskCreateDialog>

                <BlockCreateDialog>
                  <Button 
                    className={cn(tasksPageTheme.triggerButton, tasksPageTheme.createBlockButton)}
                  >
                    + Блок задач
                  </Button>
                </BlockCreateDialog>
              </>
            )}

            {canViewStatistics && (
              <Button
                onClick={() => setShowStatistics(true)}
                className={cn(tasksPageTheme.triggerButton, tasksPageTheme.statisticsButton)}
              >
                📊 Статистика задач
              </Button>
            )}
          </div>
        </div>

        <Tabs 
          value={activeTab} 
          onValueChange={(v) => setActiveTab(v as "active" | "archive" | "all")}
          className="space-y-8"
        >
          <TabsList className={tasksPageTheme.tabsList}>
            <TabsTrigger 
              value="active"
              className={tasksPageTheme.tabsTriggerActive}
            >
              Активные ({userActiveTasks.length})
            </TabsTrigger>

            <TabsTrigger 
              value="archive"
              className={tasksPageTheme.tabsTriggerArchive}
            >
              Архив ({userArchivedTasks.length})
            </TabsTrigger>

            {isAdmin && (
              <TabsTrigger 
                value="all"
                className={tasksPageTheme.tabsTriggerActive}
              >
                Все ({tasks.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <TaskList
              blocks={sortedBlocks}
              regularTasks={sortedRegularTasks}
              onBlockSelect={setSelectedBlock}
              onTaskSelect={setDetailTask}
              deadlineFilter={deadlineFilter}
              onDeadlineFilterChange={setDeadlineFilter}
            />
          </TabsContent>
        </Tabs>

        <BlockTaskDialog
          block={selectedBlock}
          open={!!selectedBlock}
          onOpenChange={() => setSelectedBlock(null)}
          onTaskUpdated={onTaskUpdated}
        />

        <TaskDetailDialog
          task={detailTask}
          open={!!detailTask}
          onOpenChange={() => setDetailTask(null)}
        />

        <TaskStatisticsDialog
          open={showStatistics}
          onOpenChange={setShowStatistics}
          tasks={tasks}
          currentUserId={userId}
        />
      </div>
    </div>
  )
}